import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BasketPageCheckoutOrderModel } from "./checkoutOrderView";
import { useApplyDiscountCodeMutation } from "./basketHooks";

interface BasketDiscountCodeInputBlockProperties {
    readonly onModelChange: (model: BasketPageCheckoutOrderModel) => void;
    readonly discountCodeAlreadyInserted: boolean;
}

export default function BasketDiscountCodeInputBlock({ onModelChange, discountCodeAlreadyInserted }: BasketDiscountCodeInputBlockProperties): React.JSX.Element {
    const { t } = useTranslation("basket");

    const [discountCode, setDiscountCode] = useState<string>("");
    const [insertedEmptyCode, setInsertedEmptyCode] = useState<boolean>(false);
    const [discountCodeError, setDiscountCodeError] = useState<string | undefined>(undefined);
    const [showAlreadyInsertedMessage, setShowAlreadyInsertedMessage] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);

    const inputVisible = open && !discountCodeAlreadyInserted;

    const codeInputReference = useRef<HTMLInputElement>(null);

    const applyDiscountCodeMutation = useApplyDiscountCodeMutation(
        discountCode,
        onModelChange,
        (errorMessage) => {
            setDiscountCodeError(errorMessage);
        },
    );

    const showInput = (): void => {
        if (discountCodeAlreadyInserted) {
            if (!showAlreadyInsertedMessage) {
                setShowAlreadyInsertedMessage(true);
                setTimeout((): void => setShowAlreadyInsertedMessage(false), 10_000);
            }

            return;
        }

        setOpen(true);
        setShowAlreadyInsertedMessage(false);
    };

    useEffect(
        (): void => {
            if (inputVisible) {
                codeInputReference.current?.focus();
            }
        },
        [inputVisible, discountCodeError, insertedEmptyCode],
    );

    const handleKeyDown = (event: React.KeyboardEvent): void => {
        if (event.key === "Enter") {
            event.preventDefault();
            handleApplyDiscountCode();
        }
    };

    const handleApplyDiscountCode = (): void => {
        setInsertedEmptyCode(false);
        setDiscountCodeError(undefined);

        if (discountCode === "") {
            setInsertedEmptyCode(true);

            return;
        }

        applyDiscountCodeMutation.mutate();
    };

    return (
        <>
            <div onClick={(): void => showInput()} className="discount-code-allow">
                <i className="fa-solid fa-tag"></i> {t("haveDiscountCode")}
            </div>

            {discountCodeAlreadyInserted && showAlreadyInsertedMessage && <div className="cart-item-wrong-discount-code">
                <i className="fa-solid fa-exclamation-circle"></i>
                <span>{t("onlyOneDiscountCode")}</span>
            </div>}

            {open
              && !discountCodeAlreadyInserted
              && <div>
                  <div className="input-group">
                      <i className="fa-solid fa-tag"></i>
                      <input
                          type="text"
                          name="discount-code"
                          className="form-control"
                          placeholder={t("enterDiscountCode")}
                          value={discountCode}
                          onChange={(event): void => setDiscountCode(event.target.value.trim().toUpperCase())}
                          onKeyDown={handleKeyDown}
                          ref={codeInputReference}
                      />
                      <span className="input-group-btn">
                          <button type="button" className="btn" onClick={handleApplyDiscountCode}>
                              {t("apply")}
                          </button>
                      </span>
                  </div>
                  {(insertedEmptyCode || discountCodeError !== undefined)
                    && <div className="cart-item-wrong-discount-code">
                        <i className="fa-solid fa-exclamation-circle"></i>
                        {discountCodeError !== undefined && <span>{discountCodeError}</span>}
                        {insertedEmptyCode && <span>{t("pleaseEnterCode")}</span>}
                    </div>}
              </div>}
        </>
    );
}
